var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { Button, Typography, Space, Spin, Statistic, Alert, message } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { Trans } from 'react-i18next';
import moment from 'moment';
import ModalWithoutBottom from '../Chat/common/ModalWithoutBottom/ModalWithoutBottom';
import useCheckIfMobile from '../../helpers/hooks/useCheckIfMobile';
import { useStore } from '../../store/rootStore';
import s from './ConnectionSpeed.module.scss';
const { Title, Text } = Typography;
// @TODO: Replace with document links from our storage
const TEST_FILES = [
    { url: '/testfiles/1MB.dat', name: '1MB.dat', size: 1 * 1024 * 1024 },
    { url: '/testfiles/5MB.zip', name: '5MB.zip', size: 5 * 1024 * 1024 },
    { url: '/testfiles/10MB.zip', name: '10MB.zip', size: 10 * 1024 * 1024 },
];
const ConnectionSpeed = () => {
    const [averageSpeed, setAverageSpeed] = useState(null);
    const [individualTestResults, setIndividualTestResults] = useState(Array(TEST_FILES.length).fill({ speed: null, duration: null, error: null }));
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [currentlyTestingFileName, setCurrentlyTestingFileName] = useState(null);
    const [averagedPerformanceMetrics, setAveragedPerformanceMetrics] = useState(null);
    const [sumOfIndividualDurations, setSumOfIndividualDurations] = useState(null);
    const [error, setError] = useState(null);
    const [connectionInfo, setConnectionInfo] = useState(null);
    const [loadingConnectionInfo, setLoadingConnectionInfo] = useState(true);
    const [loadingSpeedMetrics, setLoadingSpeedMetrics] = useState(false);
    const history = useHistory();
    const isMobile = useCheckIfMobile();
    const { exports } = useStore();
    useEffect(() => {
        const navConnection = navigator.connection;
        if (navConnection) {
            setConnectionInfo(navConnection);
        }
        setLoadingConnectionInfo(false);
    }, []);
    const onBack = () => {
        history.push('/');
    };
    const measureSpeed = () => __awaiter(void 0, void 0, void 0, function* () {
        setError(null);
        setAverageSpeed(null);
        setIndividualTestResults(Array(TEST_FILES.length).fill({ speed: null, duration: null, error: null }));
        setDownloadProgress(0);
        setAveragedPerformanceMetrics(null);
        setSumOfIndividualDurations(null);
        setLoadingSpeedMetrics(true);
        setCurrentlyTestingFileName(null);
        let totalMbps = 0;
        let successfulTests = 0;
        let currentSumOfDurations = 0;
        let totalDuration = 0;
        let totalTcpHandshake = 0;
        let totalDnsLookup = 0;
        let totalTtfb = 0;
        let performanceDataCount = 0;
        for (let i = 0; i < TEST_FILES.length; i++) {
            const testFile = TEST_FILES[i];
            setCurrentlyTestingFileName(testFile.name);
            setDownloadProgress(0);
            let resultForThisFile = { speed: null, duration: null, error: null };
            try {
                const markStart = 'fetch-start-' + testFile.url.replace(/[^a-zA-Z0-9]/g, '');
                const markEnd = 'fetch-end-' + testFile.url.replace(/[^a-zA-Z0-9]/g, '');
                const measureName = 'fetch-duration-' + testFile.url.replace(/[^a-zA-Z0-9]/g, '');
                performance.clearMarks(markStart);
                performance.clearMarks(markEnd);
                performance.clearMeasures(measureName);
                performance.mark(markStart);
                const overallStartTime = performance.now();
                const response = yield fetch(`${testFile.url}?cb=${new Date().getTime()}`, { cache: 'no-store' });
                if (!response.ok) {
                    throw new Error(`HTTP error for ${testFile.url}! status: ${response.status}`);
                }
                if (!response.body) {
                    throw new Error('ReadableStream not yet supported in this browser.');
                }
                const reader = response.body.getReader();
                let receivedLength = 0;
                const chunks = [];
                while (true) {
                    const { done, value } = yield reader.read();
                    if (done) {
                        break;
                    }
                    chunks.push(value);
                    receivedLength += value.length;
                    // Update progress
                    const progress = Math.round((receivedLength / testFile.size) * 100);
                    setDownloadProgress(progress);
                }
                const overallEndTime = performance.now();
                performance.mark(markEnd);
                performance.measure(measureName, markStart, markEnd);
                const entries = performance.getEntriesByType('resource');
                const resourceEntry = entries.find((entry) => entry.name.includes(testFile.url));
                if (resourceEntry) {
                    totalDuration += resourceEntry.duration;
                    totalTcpHandshake += resourceEntry.connectEnd - resourceEntry.connectStart;
                    totalDnsLookup += resourceEntry.domainLookupEnd - resourceEntry.domainLookupStart;
                    totalTtfb += resourceEntry.responseStart - resourceEntry.requestStart;
                    performanceDataCount++;
                }
                let bytesTransferred = testFile.size;
                if (resourceEntry && resourceEntry.transferSize > 0) {
                    bytesTransferred = resourceEntry.transferSize;
                }
                else if (resourceEntry && resourceEntry.decodedBodySize > 0) {
                    bytesTransferred = resourceEntry.decodedBodySize;
                }
                let durationInSeconds = (overallEndTime - overallStartTime) / 1000;
                if (resourceEntry && resourceEntry.duration > 0) {
                    const resourceDurationSeconds = resourceEntry.duration / 1000;
                    if (resourceDurationSeconds > 0.001)
                        durationInSeconds = resourceDurationSeconds;
                }
                if (durationInSeconds <= 0) {
                    resultForThisFile = { speed: null, duration: durationInSeconds, error: 'Error (duration ~0)' };
                }
                else {
                    const speedBps = (bytesTransferred * 8) / durationInSeconds;
                    const speedMbps = speedBps / 1024 / 1024;
                    resultForThisFile = {
                        speed: `${speedMbps.toFixed(2)} Mbps`,
                        duration: durationInSeconds,
                        error: null,
                    };
                    totalMbps += speedMbps;
                    successfulTests++;
                    currentSumOfDurations += durationInSeconds;
                }
            }
            catch (fileError) {
                console.error(`Error measuring speed for ${testFile.url}:`, fileError);
                resultForThisFile = { speed: null, duration: null, error: `Error: ${fileError.message}` };
            }
            setIndividualTestResults((prev) => {
                const newResults = [...prev];
                newResults[i] = resultForThisFile;
                return newResults;
            });
        }
        setCurrentlyTestingFileName(null);
        if (successfulTests > 0) {
            const avgMbps = totalMbps / successfulTests;
            setAverageSpeed(avgMbps.toFixed(2));
            setSumOfIndividualDurations(currentSumOfDurations);
        }
        if (performanceDataCount > 0) {
            setAveragedPerformanceMetrics({
                duration: totalDuration / performanceDataCount,
                tcpHandshake: totalTcpHandshake / performanceDataCount,
                dnsLookup: totalDnsLookup / performanceDataCount,
                ttfb: totalTtfb / performanceDataCount,
            });
        }
        else if (individualTestResults.some((r) => r.error) && successfulTests === 0) {
            setError('All speed tests failed to produce a valid measurement or encountered errors.');
        }
        setLoadingSpeedMetrics(false);
    });
    const triggerCsvDownload = (csvString, filename) => {
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    };
    const handleExportReport = () => {
        const csvData = exports.generateConnectionSpeedReportCSV(TEST_FILES, connectionInfo, averageSpeed, individualTestResults, averagedPerformanceMetrics, sumOfIndividualDurations);
        const reportDate = moment().format('YYYY-MM-DD HH:mm:ss');
        triggerCsvDownload(csvData, `connection_speed_report_${reportDate}.csv`);
        message.success(React.createElement(Trans, { i18nKey: "reportDownloadStarted" }));
    };
    return (React.createElement(ModalWithoutBottom, { setModalVisible: onBack, titleTextKey: "connectionSpeedTest", width: isMobile ? undefined : 920, visible: true },
        React.createElement("div", { className: s.root },
            React.createElement("div", { className: s.topControls },
                React.createElement(Button, { type: "primary", onClick: measureSpeed, className: s.button, loading: loadingSpeedMetrics },
                    React.createElement(Trans, { i18nKey: "measureDownloadSpeed" })),
                React.createElement("div", { className: s.averageSpeedStatistic },
                    React.createElement(Statistic, { title: React.createElement(Trans, { i18nKey: "averageDownloadSpeed" }), value: loadingSpeedMetrics && !averageSpeed ? ' ' : averageSpeed || '-', precision: loadingSpeedMetrics && !averageSpeed ? 0 : 2, suffix: loadingSpeedMetrics && !averageSpeed ? null : averageSpeed ? 'Mbps' : null, prefix: loadingSpeedMetrics && !averageSpeed ? React.createElement(Spin, { size: "small" }) : null }))),
            error && (React.createElement("div", { className: s.resultItem },
                React.createElement(Alert, { message: error, type: "error", showIcon: true }))),
            React.createElement("div", { className: s.allResultsContainer },
                React.createElement("div", { className: s.results },
                    React.createElement(Title, { level: 5 }, "Individual Test Speeds:"),
                    React.createElement(Space, { direction: "vertical", style: { width: '100%' } }, TEST_FILES.map((file, index) => {
                        var _a;
                        const currentResult = individualTestResults[index];
                        const isTestingThisFile = loadingSpeedMetrics &&
                            currentlyTestingFileName === file.name &&
                            !currentResult.speed &&
                            !currentResult.error;
                        const isDone = !!(currentResult.speed || currentResult.error);
                        const isPending = loadingSpeedMetrics && !isDone && !isTestingThisFile;
                        const hasNotStarted = !loadingSpeedMetrics && !isDone;
                        let statusIcon = null;
                        let statusText = 'Pending...';
                        if (isDone) {
                            if (currentResult.error) {
                                statusIcon = React.createElement(MinusCircleOutlined, { style: { color: 'red' } });
                                statusText = currentResult.error;
                            }
                            else if (currentResult.speed) {
                                statusIcon = React.createElement(CheckCircleOutlined, { style: { color: 'green' } });
                                statusText = `${currentResult.speed} (${(_a = currentResult.duration) === null || _a === void 0 ? void 0 : _a.toFixed(1)}s)`;
                            }
                            else {
                                // Should not happen if error or speed is present
                                statusIcon = React.createElement(MinusCircleOutlined, null);
                                statusText = 'Completed (unknown state)';
                            }
                        }
                        else if (isTestingThisFile) {
                            statusIcon = React.createElement(Spin, { size: "small" });
                            statusText = `Downloading... ${downloadProgress}%`;
                        }
                        else if (isPending) {
                            statusIcon = React.createElement(ClockCircleOutlined, null);
                            statusText = 'Pending...';
                        }
                        else if (hasNotStarted) {
                            statusIcon = React.createElement(MinusCircleOutlined, null);
                            statusText = 'Waiting to start...';
                        }
                        return (React.createElement("div", { key: file.url, className: s.individualTestItem },
                            React.createElement("span", { className: s.testFileName }, file.name),
                            React.createElement("div", { className: s.statusDetailsGroup },
                                React.createElement("span", { className: s.testFileStatusIcon }, statusIcon),
                                React.createElement("span", { className: s.testFileStatusText }, statusText))));
                    }))),
                React.createElement("div", { className: s.dataRow },
                    React.createElement("div", { className: s.results },
                        React.createElement(Title, { level: 5 }, "Navigator Connection API Data:"),
                        React.createElement(Space, { direction: "vertical" },
                            React.createElement(Text, { className: s.resultItem },
                                "Effective Type:",
                                ' ',
                                loadingConnectionInfo ? (React.createElement(Spin, { size: "small" })) : ((connectionInfo === null || connectionInfo === void 0 ? void 0 : connectionInfo.effectiveType) || 'N/A')),
                            React.createElement(Text, { className: s.resultItem },
                                "Downlink:",
                                ' ',
                                loadingConnectionInfo ? (React.createElement(Spin, { size: "small" })) : (connectionInfo === null || connectionInfo === void 0 ? void 0 : connectionInfo.downlink) ? (`${connectionInfo.downlink} Mbps`) : ('N/A')),
                            React.createElement(Text, { className: s.resultItem },
                                "RTT:",
                                ' ',
                                loadingConnectionInfo ? (React.createElement(Spin, { size: "small" })) : (connectionInfo === null || connectionInfo === void 0 ? void 0 : connectionInfo.rtt) ? (`${connectionInfo.rtt} ms`) : ('N/A')),
                            React.createElement(Text, { className: s.resultItem },
                                "Save Data:",
                                ' ',
                                loadingConnectionInfo ? (React.createElement(Spin, { size: "small" })) : (connectionInfo === null || connectionInfo === void 0 ? void 0 : connectionInfo.saveData) !== undefined ? (connectionInfo.saveData ? ('Yes') : ('No')) : ('N/A')))),
                    React.createElement("div", { className: s.results },
                        React.createElement(Title, { level: 5 }, "Performance API Data:"),
                        loadingSpeedMetrics && !averagedPerformanceMetrics ? (React.createElement("div", { style: { textAlign: 'center', padding: '20px' } },
                            React.createElement(Spin, { tip: "Calculating averages..." }))) : averagedPerformanceMetrics ? (React.createElement(Space, { direction: "vertical" },
                            React.createElement(Text, { className: s.resultItem },
                                "Avg. Duration: ",
                                averagedPerformanceMetrics.duration.toFixed(2),
                                " ms"),
                            React.createElement(Text, { className: s.resultItem },
                                "Avg. TCP Handshake: ",
                                averagedPerformanceMetrics.tcpHandshake.toFixed(2),
                                " ms"),
                            React.createElement(Text, { className: s.resultItem },
                                "Avg. DNS Lookup: ",
                                averagedPerformanceMetrics.dnsLookup.toFixed(2),
                                " ms"),
                            React.createElement(Text, { className: s.resultItem },
                                "Avg. Time to First Byte (TTFB): ",
                                averagedPerformanceMetrics.ttfb.toFixed(2),
                                " ms"),
                            sumOfIndividualDurations !== null && (React.createElement(Text, { className: s.resultItem },
                                "Total Download Duration: ",
                                sumOfIndividualDurations.toFixed(1),
                                " s")))) : (React.createElement(Text, null, "Click \"Measure Download Speed\" to see performance data."))))),
            React.createElement("div", { className: s.exportButtonContainer },
                React.createElement(Button, { onClick: handleExportReport, type: "primary", disabled: !averageSpeed || loadingSpeedMetrics },
                    React.createElement(Trans, { i18nKey: "exportReport" }))))));
};
export default ConnectionSpeed;
